import { memo, useCallback, useMemo } from 'react';

import {
  type DeepPartial,
  type FieldPath,
  useController,
} from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import type {
  GetPropertyTypesQuery,
  PropertySearchFormFragment,
  Property_Searches_Set_Input,
  UserSelectFragment,
} from '@graphql/__generated__/graphql';
import { useAddUserModal } from '@modules/_shared/components/AddUserModal';
import {
  type FormDefinitionType,
  type FormFieldDefinitionType,
  RaForm,
  createCategoryElement,
  createCheckboxElement,
  createCheckboxGroupElement,
  createSelectElement,
  createTextFieldElement,
} from '@modules/_shared/components/form/RaForm';
import { PlacePicker } from '@modules/_shared/components/place-picker/PlacePicker';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getCurrencySymbol } from '@modules/_shared/utils/formatting';
import { PropertyTypesTree } from '@modules/property-searches/components/PropertyTypesTree';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';

export type PropertySearchFormData = Pick<
  NonNullable<Property_Searches_Set_Input>,
  | 'notification'
  | 'offer_type'
  | 'has_balcony'
  | 'has_garden'
  | 'has_lift'
  | 'has_parking'
  | 'has_swimming_pool'
  | 'has_view'
  | 'is_child_friendly'
  | 'is_fsbo'
  | 'is_furnished'
  | 'is_minergie_certified'
  | 'is_new_development'
  | 'is_wheelchair_accessible'
  | 'activate_aggregator'
  | 'comment'
  | 'notification'
  | 'property_usage'
> & {
  user: string | null;
  places: string[];
  property_types: PropertyType[];
  sale_price: {
    gte: number | null;
    lte: number | null;
  };
  monthly_rent: {
    gte: number | null;
    lte: number | null;
  };
  number_of_rooms: {
    gte: number | null;
    lte: number | null;
  };
  number_of_bathrooms: {
    gte: number | null;
    lte: number | null;
  };
  number_of_bedrooms: {
    gte: number | null;
    lte: number | null;
  };
  living_surface: {
    gte: number | null;
    lte: number | null;
  };
  built_surface: {
    gte: number | null;
    lte: number | null;
  };
  land_surface: {
    gte: number | null;
    lte: number | null;
  };
  sale_price_per_land_surface: {
    gte: number | null;
    lte: number | null;
  };
  sale_price_per_living_surface: {
    gte: number | null;
    lte: number | null;
  };
  gross_rent_room_monthly: {
    gte: number | null;
    lte: number | null;
  };
  gross_rent_m2_yearly: {
    gte: number | null;
    lte: number | null;
  };
  property_status: 'both' | 'new' | 'existing';
};

type PropertyType = GetPropertyTypesQuery['property_types'][number];

export const PROPERTY_SEARCH_FORM_FRAGMENT = gql(/* GraphQL */ `
  fragment PropertySearchForm on property_searches {
    property_types {
      property_type {
        id
        label
        type
        composite_type
        composite_type_object {
          id
          label
          name
        }
      }
    }
    property_searches_places {
      place {
        id
        name
        label
      }
    }
    user {
      id
      ...UserCard_user
    }
    offer_type
    notification
    property_status
    comment
    property_usage
    activate_aggregator
    sale_price_lte
    sale_price_gte
    sale_price_per_land_surface_gte
    sale_price_per_land_surface_lte
    sale_price_per_living_surface_gte
    sale_price_per_living_surface_lte
    monthly_rent_lte
    monthly_rent_gte
    gross_rent_room_monthly_gte
    gross_rent_room_monthly_lte
    gross_rent_m2_yearly_gte
    gross_rent_m2_yearly_lte
    number_of_rooms_lte
    number_of_rooms_gte
    number_of_bathrooms_lte
    number_of_bathrooms_gte
    number_of_bedrooms_lte
    number_of_bedrooms_gte
    living_surface_lte
    living_surface_gte
    built_surface_lte
    built_surface_gte
    land_surface_lte
    land_surface_gte
    has_balcony
    has_garden
    has_lift
    has_parking
    has_swimming_pool
    has_view
    is_child_friendly
    is_fsbo
    is_furnished
    is_minergie_certified
    is_new_development
    is_wheelchair_accessible
  }
`);

type PropertySearchFormProps = {
  propertySearch?: PropertySearchFormFragment | null;
  onSubmit: (data: PropertySearchFormData) => Promise<any>;
  includeContact?: boolean;
  preselectedContact?: string;
  onClose: () => void;
};

const createRangeField = (
  name: FieldPath<PropertySearchFormData>,
  label: string,
  options?: {
    render?: (data: PropertySearchFormData) => boolean;
    decimalNumbers?: number;
    prefix?: string;
    suffix?: string;
  },
): FormFieldDefinitionType<PropertySearchFormData> => {
  const { render, ...inputOptions } = options ?? {};
  return {
    type: 'range',
    name,
    label,
    gridProps: { md: 12 },
    slotProps: {
      minInput: {
        name: `${name}.gte`,
        ...inputOptions,
      },
      maxInput: {
        name: `${name}.lte`,
        ...inputOptions,
      },
    },
    render,
  };
};

const formDefinition: FormDefinitionType<
  PropertySearchFormData,
  {
    currencySymbol: string;
    includeContact: boolean;
    disableContact: boolean;
    isCHCountry: boolean;
    isCHRATenant: boolean;
    onUserCreateSelected: (userData: {
      first_name: string | null;
      last_name: string | null;
      email: string | null;
    }) => Promise<UserSelectFragment | undefined>;
  }
> = ({ t, context }) => {
  const surfaceMiscGroupFields = (
    [
      createCategoryElement('surface_title', t('Surfaces')),
      createRangeField('living_surface', t('Living surface')),
      createRangeField('built_surface', t('Built surface')),
      createRangeField('land_surface', t('Land surface')),
      createCategoryElement('misc_title', t('Miscellaneous')),
      context.isCHRATenant
        ? createCheckboxElement(
            'activate_aggregator',
            t('Activate aggregator alerts'),
            { gridProps: { md: 12 } },
          )
        : null,
      context.isCHRATenant
        ? createSelectElement(
            'notification',
            t('Notification'),
            () => [
              { value: 'daily', label: t('Daily') },
              { value: 'weekly', label: t('Weekly') },
              { value: 'never', label: t('Never') },
            ],
            { gridProps: { md: 12 } },
          )
        : null,
      createTextFieldElement('comment', t('Comment'), {
        multiline: true,
        gridProps: { md: 12 },
      }),
    ] as FormFieldDefinitionType<PropertySearchFormData>[]
  ).filter(v => v != null);
  const fieldsDefinitions: FormFieldDefinitionType<PropertySearchFormData>[] = [
    {
      name: 'offer_type',
      type: 'select',
      toggleButtons: true,
      label: t('Offer type'),
      options: () => [
        { value: 'buy', label: t('Buy') },
        { value: 'rent', label: t('Rent') },
      ],
      gridProps: { xs: 6 },
    },
    createSelectElement(
      'property_status',
      t('Property status'),
      () => [
        { value: 'both', label: t('New or existing') },
        { value: 'new', label: t('New') },
        { value: 'existing', label: t('Existing') },
      ],
      { gridProps: { xs: 6 } },
    ),
    createCategoryElement('location_title', t('Location')),
    {
      name: 'property_searches_places',
      type: 'custom',
      element: <PropertySearchPlacePicker />,
      gridProps: { md: 12 },
    },
    {
      name: 'price_group',
      type: 'group',
      fields: [
        createCategoryElement('price_title', t('Prices')),
        createRangeField('sale_price', t('Sale price'), {
          render: ({ offer_type }) => offer_type === 'buy',
          decimalNumbers: 2,
          prefix: context.currencySymbol,
        }),
        createRangeField(
          'sale_price_per_land_surface',
          t('Sale price per land surface'),
          {
            render: ({ offer_type }) => offer_type === 'buy',
            decimalNumbers: 2,
            prefix: context.currencySymbol,
          },
        ),
        createRangeField(
          'sale_price_per_living_surface',
          t('Sale price per living surface'),
          {
            render: ({ offer_type }) => offer_type === 'buy',
            decimalNumbers: 2,
            prefix: context.currencySymbol,
          },
        ),
        createRangeField('monthly_rent', t('Monthly rent'), {
          render: ({ offer_type }) => offer_type === 'rent',
          decimalNumbers: 2,
          prefix: context.currencySymbol,
        }),
        createRangeField(
          'gross_rent_room_monthly',
          t('Gross rent per room monthly'),
          {
            render: ({ offer_type }) => offer_type === 'rent',
            decimalNumbers: 2,
            prefix: context.currencySymbol,
          },
        ),
        createRangeField(
          'gross_rent_m2_yearly',
          t('Gross rent per m² yearly'),
          {
            render: ({ offer_type }) => offer_type === 'rent',
            decimalNumbers: 2,
            prefix: context.currencySymbol,
          },
        ),
      ],
      gridProps: { sm: 6 },
    },
    {
      name: 'rooms_group',
      type: 'group',
      fields: [
        createCategoryElement('rooms_title', t('Rooms')),
        createRangeField('number_of_rooms', t('Number of rooms'), {
          decimalNumbers: context.isCHCountry ? 1 : 0,
        }),
        createRangeField('number_of_bedrooms', t('Number of bedrooms')),
        createRangeField('number_of_bathrooms', t('Number of bathrooms')),
      ],
      gridProps: { sm: 6 },
    },
    {
      name: 'surface_misc_group',
      type: 'group',
      fields: surfaceMiscGroupFields,
      gridProps: { sm: 6 },
    },
    {
      name: 'property_group',
      type: 'group',
      fields: [
        createCategoryElement('property_title', t('Property')),
        createSelectElement(
          'property_usage',
          t('Property usage'),
          () => [
            { value: 'primary', label: t('Primary') },
            { value: 'secondary', label: t('Secondary') },
            { value: 'investment', label: t('Investment') },
          ],
          { gridProps: { md: 12 } },
        ),
        {
          name: 'property_types',
          type: 'custom',
          element: <PropertyTypesSelect />,
          gridProps: { md: 12 },
        },
      ],
      gridProps: { sm: 6 },
    },
    createCategoryElement('features_title', t('Equipment & Characteristics')),
    createCheckboxGroupElement(
      'features',
      t('Features'),
      [
        createCheckboxElement('is_child_friendly', t('Child friendly')),
        createCheckboxElement('is_fsbo', t('FSBO')),
        createCheckboxElement('is_furnished', t('Furnished')),
        createCheckboxElement('is_minergie_certified', t('Minergie certified')),
        createCheckboxElement(
          'is_wheelchair_accessible',
          t('Wheelchair accessible'),
        ),
        createCheckboxElement('is_new_development', t('New development')),
      ],
      {
        gridProps: { sm: 6 },
      },
    ),
    createCheckboxGroupElement(
      'equipment',
      t('Equipment'),
      [
        createCheckboxElement('has_balcony', t('Balcony')),
        createCheckboxElement('has_garden', t('Garden')),
        createCheckboxElement('has_lift', t('Lift')),
        createCheckboxElement('has_parking', t('Parking')),
        createCheckboxElement('has_swimming_pool', t('Swimming pool')),
        createCheckboxElement('has_view', t('Unobstructed view')),
      ],
      {
        gridProps: { sm: 6 },
      },
    ),
  ];

  if (context.includeContact) {
    fieldsDefinitions.splice(
      2,
      0,
      createCategoryElement('contact_title', t('Contact')),
      {
        name: 'user',
        type: 'user',
        label: t('User'),
        required: true,
        createUserSelected: context.onUserCreateSelected,
        gridProps: { md: 12 },
        disabled: () => context.disableContact ?? false,
      },
    );
  }

  return fieldsDefinitions;
};

export const PropertyTypesSelect: React.FC = memo(() => {
  const { field, fieldState } = useController<
    PropertySearchFormData,
    'property_types'
  >({ name: 'property_types' });

  return (
    <PropertyTypesTree
      values={field.value ?? []}
      onChange={field.onChange}
      validationError={fieldState.error?.message}
    />
  );
});

export const PropertySearchPlacePicker: React.FC = memo(() => {
  const {
    field: { onChange },
    formState: { defaultValues },
  } = useController<PropertySearchFormData, 'places'>({
    name: 'places',
  });

  const places = useMemo(
    () => defaultValues?.places?.filter(place => place != null) ?? [],
    [defaultValues?.places],
  );

  return (
    <PlacePicker
      defaultValue={places}
      onChange={onChange}
      fullHeight={false}
      condensed
    />
  );
});

export const PropertySearchForm: React.FC<
  React.PropsWithChildren<PropertySearchFormProps>
> = ({
  propertySearch,
  children,
  onSubmit,
  includeContact = false,
  preselectedContact,
  onClose,
}) => {
  const { me } = useAppData();
  const { locale, countryCode } = useLocale();
  const [AddUserModal, openAddUserModal] = useAddUserModal();

  const currencySymbol = useMemo(() => {
    // TODO: Maybe try to determine a country from the places linked to the property search?
    const computedCurrency = getCurrencyByCountryCode(
      me?.tenant.country_code ?? countryCode,
    );
    return getCurrencySymbol(computedCurrency, locale);
  }, [locale, countryCode, me?.tenant.country_code]);

  const defaultValues = useMemo<DeepPartial<PropertySearchFormData>>(
    () => ({
      offer_type: propertySearch?.offer_type ?? 'buy',
      sale_price: {
        gte: propertySearch?.sale_price_gte,
        lte: propertySearch?.sale_price_lte,
      },
      monthly_rent: {
        gte: propertySearch?.monthly_rent_gte,
        lte: propertySearch?.monthly_rent_lte,
      },
      sale_price_per_land_surface: {
        gte: propertySearch?.sale_price_per_land_surface_gte,
        lte: propertySearch?.sale_price_per_land_surface_lte,
      },
      sale_price_per_living_surface: {
        gte: propertySearch?.sale_price_per_living_surface_gte,
        lte: propertySearch?.sale_price_per_living_surface_lte,
      },
      number_of_rooms: {
        gte: propertySearch?.number_of_rooms_gte,
        lte: propertySearch?.number_of_rooms_lte,
      },
      number_of_bedrooms: {
        gte: propertySearch?.number_of_bedrooms_gte,
        lte: propertySearch?.number_of_bedrooms_lte,
      },
      number_of_bathrooms: {
        gte: propertySearch?.number_of_bathrooms_gte,
        lte: propertySearch?.number_of_bathrooms_lte,
      },
      built_surface: {
        gte: propertySearch?.built_surface_gte,
        lte: propertySearch?.built_surface_lte,
      },
      living_surface: {
        gte: propertySearch?.living_surface_gte,
        lte: propertySearch?.living_surface_lte,
      },
      land_surface: {
        gte: propertySearch?.land_surface_gte,
        lte: propertySearch?.land_surface_lte,
      },
      property_types:
        propertySearch?.property_types.map(item => item.property_type) ?? [],
      places:
        propertySearch?.property_searches_places.map(item => item.place.id) ??
        [],
      is_child_friendly: propertySearch?.is_child_friendly ?? false,
      is_wheelchair_accessible:
        propertySearch?.is_wheelchair_accessible ?? false,
      is_new_development: propertySearch?.is_new_development ?? false,
      is_fsbo: propertySearch?.is_fsbo ?? false,
      is_furnished: propertySearch?.is_furnished ?? false,
      is_minergie_certified: propertySearch?.is_minergie_certified ?? false,
      has_view: propertySearch?.has_view ?? false,
      has_swimming_pool: propertySearch?.has_swimming_pool ?? false,
      has_balcony: propertySearch?.has_balcony ?? false,
      has_garden: propertySearch?.has_garden ?? false,
      has_lift: propertySearch?.has_lift ?? false,
      has_parking: propertySearch?.has_parking ?? false,
      activate_aggregator: propertySearch?.activate_aggregator ?? false,
      comment: propertySearch?.comment,
      notification: propertySearch?.notification ?? 'never',
      property_usage: propertySearch?.property_usage ?? 'primary',
      property_status:
        (propertySearch?.property_status as
          | 'new'
          | 'existing'
          | undefined
          | null) ?? 'both',
      user: preselectedContact ?? propertySearch?.user?.id ?? null,
    }),
    [propertySearch, preselectedContact],
  );

  const onUserCreateSelected = useCallback(
    async (userData: {
      first_name: string | null;
      last_name: string | null;
      email: string | null;
    }) => {
      const newUser: {
        firstName?: string;
        lastName?: string;
        email?: string;
      } = {};

      if (userData.first_name != null) {
        newUser.firstName = userData.first_name;
      }

      if (userData.last_name != null) {
        newUser.lastName = userData.last_name;
      }

      if (userData.email != null) {
        newUser.email = userData.email;
      }

      return openAddUserModal(newUser);
    },
    [openAddUserModal],
  );

  return (
    <>
      <RaForm
        formDefinition={formDefinition}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        onCancel={onClose}
        context={{
          currencySymbol,
          includeContact,
          onUserCreateSelected,
          disableContact: preselectedContact != null,
          isCHCountry: me?.tenant.country_code === 'CH',
          isCHRATenant: me?.tenant.slug === 'ch',
        }}
      >
        {children}
      </RaForm>
      {includeContact && AddUserModal}
    </>
  );
};
